import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  Alert,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import AppDialog from "../General/Dialog/AppDialog";
import ConfirmDialog from "../General/Dialog/ConfirmDialog";
import LiquidationFormDialog from "./LiquidationFormDialog";
import {
  getLiquidationsByLegalFile,
  deleteLiquidation,
} from "../../services/LiquidationService";
import { downloadLegalFileDocument } from "../../services/LegalFileAttachmentService";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { useTheme } from "@mui/material/styles";
import { formatCurrency } from "../../config/Helper";
import { isLegalFileLocked } from "../LegalFile/Detail/detailHelpers";

// ── Constants ─────────────────────────────────────────────────────────────────

const CATEGORY_LABELS = {
  capital: "Capital",
  honorarios: "Honorarios",
  gastos: "Gastos",
};

const CALC_LABELS = {
  jus_multiplier: "JUS",
  percentage_of_amount: "% Monto",
  interest_on_tax: "Interés",
  fixed_catalog: "Catálogo",
  manual: "Manual",
};

function fmtDate(v) {
  if (!v) return "—";
  return new Date(v).toLocaleDateString("es-AR");
}

// ── Glass badge ───────────────────────────────────────────────────────────────

function GlassBadge({ children, isDark, sx = {} }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1,
        py: 0.3,
        borderRadius: 1,
        background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.055)",
        border: isDark
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(0,0,0,0.1)",
        backdropFilter: "blur(8px)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

// ── MetaPill ──────────────────────────────────────────────────────────────────

function MetaPill({ label, value, isDark }) {
  return (
    <Box
      sx={{
        px: 1.6,
        py: 1,
        borderRadius: 2,
        border: isDark
          ? "1px solid rgba(255,255,255,0.09)"
          : "1px solid rgba(0,0,0,0.09)",
        background: isDark
          ? "rgba(255,255,255,0.04)"
          : "rgba(255,255,255,0.65)",
        backdropFilter: "blur(10px)",
        minWidth: 100,
      }}
    >
      <Typography
        sx={{
          opacity: 0.5,
          display: "block",
          fontWeight: 700,
          fontSize: 9,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          mb: 0.3,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 900, fontSize: 13, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  );
}

// ── CategorySection ───────────────────────────────────────────────────────────

// La Municipalidad pide los rubros de Honorarios en JUS (monto en pesos ÷
// valor de JUS de esta liquidación) en vez de pesos. Solo presentación.
function formatHonorarios(amount, jusValue, honorariosInJus) {
  if (honorariosInJus && Number(jusValue) > 0) {
    return `${(Number(amount) / Number(jusValue)).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} JUS`;
  }
  return formatCurrency(amount);
}

function CategorySection({ category, items, isDark, jusValue, honorariosInJus }) {
  if (!items.length) return null;

  const subtotal = items.reduce((s, i) => s + Number(i.amount ?? 0), 0);
  const subtotalIva = items.reduce((s, i) => s + Number(i.iva_amount ?? 0), 0);
  const isHonorarios = category === "honorarios";

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          py: 0.9,
          borderRadius: "8px 8px 0 0",
          background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.025)",
          border: isDark
            ? "1px solid rgba(255,255,255,0.09)"
            : "1px solid rgba(0,0,0,0.07)",
          borderBottom: "none",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <GlassBadge isDark={isDark}>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                lineHeight: 1,
                opacity: isDark ? 0.85 : 0.7,
              }}
            >
              {CATEGORY_LABELS[category] ?? category}
            </Typography>
          </GlassBadge>
          <Typography sx={{ fontSize: 10, opacity: 0.45, fontWeight: 600 }}>
            {items.length} {items.length === 1 ? "rubro" : "rubros"}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography sx={{ fontWeight: 900, fontSize: 13 }}>
            {isHonorarios ? formatHonorarios(subtotal, jusValue, honorariosInJus) : formatCurrency(subtotal)}
          </Typography>
          {subtotalIva > 0 && (
            <Typography sx={{ fontSize: 10, opacity: 0.55 }}>
              + IVA {isHonorarios ? formatHonorarios(subtotalIva, jusValue, honorariosInJus) : formatCurrency(subtotalIva)}
            </Typography>
          )}
        </Box>
      </Box>

      <TableContainer
        sx={{
          border: isDark
            ? "1px solid rgba(255,255,255,0.09)"
            : "1px solid rgba(0,0,0,0.07)",
          borderTop: "none",
          borderRadius: "0 0 8px 8px",
          background: isDark
            ? "rgba(255,255,255,0.02)"
            : "rgba(255,255,255,0.5)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Table size="small">
          <TableBody>
            {items.map((item, idx) => {
              const meta = item.metadata ?? {};
              return (
                <TableRow
                  key={item.id ?? idx}
                  sx={{
                    bgcolor:
                      idx % 2 === 0
                        ? isDark
                          ? "rgba(255,255,255,0.015)"
                          : "rgba(255,255,255,0.6)"
                        : "transparent",
                    "&:last-child td": { borderBottom: 0 },
                  }}
                >
                  <TableCell sx={{ fontWeight: 700, fontSize: 12, py: 0.9, pl: 1.5, width: "22%" }}>
                    {item.item_type?.name ?? "—"}
                  </TableCell>
                  <TableCell sx={{ py: 0.9, width: "10%" }}>
                    <Typography sx={{ fontSize: 10, opacity: 0.5, fontWeight: 600 }}>
                      {CALC_LABELS[item.item_type?.calculation_type] ?? "—"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: 11, py: 0.9, opacity: 0.7, width: "14%" }}>
                    {meta.desde ? (
                      <Box>
                        <Typography sx={{ display: "block", opacity: 0.55, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>Desde</Typography>
                        {meta.desde}
                      </Box>
                    ) : "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: 11, py: 0.9, opacity: 0.7, width: "14%" }}>
                    {meta.hasta ? (
                      <Box>
                        <Typography sx={{ display: "block", opacity: 0.55, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>Hasta</Typography>
                        {meta.hasta}
                      </Box>
                    ) : "—"}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 11, py: 0.9, opacity: 0.75, width: "10%" }}>
                    {meta.tasa > 0 ? (
                      <Box>
                        <Typography sx={{ display: "block", opacity: 0.55, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, textAlign: "right" }}>Tasa</Typography>
                        {meta.tasa}%
                      </Box>
                    ) : "—"}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.9, width: "15%" }}>
                    <Typography sx={{ fontWeight: 900, fontSize: 13 }}>
                      {isHonorarios ? formatHonorarios(item.amount, jusValue, honorariosInJus) : formatCurrency(item.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 11, py: 0.9, opacity: 0.7, width: "15%", pr: 1.5 }}>
                    {Number(item.iva_amount) > 0 ? (
                      <Box>
                        <Typography sx={{ display: "block", opacity: 0.55, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, textAlign: "right" }}>IVA 21%</Typography>
                        {isHonorarios ? formatHonorarios(item.iva_amount, jusValue, honorariosInJus) : formatCurrency(item.iva_amount)}
                      </Box>
                    ) : "—"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ── TotalsSection ─────────────────────────────────────────────────────────────

function TotalsSection({ items, isDark, jusValue, honorariosInJus }) {
  const totalCapital = (items ?? []).filter((i) => i.item_type?.category === "capital").reduce((s, i) => s + Number(i.amount ?? 0), 0);
  const totalHonorarios = (items ?? []).filter((i) => i.item_type?.category === "honorarios").reduce((s, i) => s + Number(i.amount ?? 0), 0);
  const totalIva = (items ?? []).reduce((s, i) => s + Number(i.iva_amount ?? 0), 0);
  const totalGastos = (items ?? []).filter((i) => i.item_type?.category === "gastos").reduce((s, i) => s + Number(i.amount ?? 0), 0);
  const grand = totalCapital + totalHonorarios + totalIva + totalGastos;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        flexWrap: "wrap",
        alignItems: "stretch",
        p: 2,
        borderRadius: 2.5,
        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.65)",
        backdropFilter: "blur(12px)",
        border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.07)",
      }}
    >
      {[
        { label: "Capital", value: totalCapital },
        { label: "Gastos", value: totalGastos },
      ].map(({ label, value }) => (
        <Box
          key={label}
          sx={{
            flex: "1 1 90px",
            minWidth: 80,
            px: 1.5,
            py: 1,
            borderRadius: 1.5,
            background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
            border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.06)",
          }}
        >
          <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, opacity: 0.5, display: "block", mb: 0.3 }}>
            {label}
          </Typography>
          <Typography sx={{ fontWeight: 900, fontSize: 13 }}>
            {formatCurrency(value)}
          </Typography>
        </Box>
      ))}

      {/* Honorarios + IVA agrupados */}
      <Box
        sx={{
          flex: "1 1 110px",
          minWidth: 90,
          px: 1.5,
          py: 1,
          borderRadius: 1.5,
          background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
          border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, opacity: 0.5, display: "block", mb: 0.3 }}>
          Honorarios
        </Typography>
        <Typography sx={{ fontWeight: 900, fontSize: 13 }}>
          {formatHonorarios(totalHonorarios, jusValue, honorariosInJus)}
        </Typography>
        {totalIva > 0 && (
          <Box
            sx={{
              mt: 0.6,
              pt: 0.6,
              borderTop: isDark ? "1px dashed rgba(255,255,255,0.12)" : "1px dashed rgba(0,0,0,0.12)",
            }}
          >
            <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, opacity: 0.45, display: "block", mb: 0.2 }}>
              + IVA (21%)
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: 12, opacity: 0.8 }}>
              {formatHonorarios(totalIva, jusValue, honorariosInJus)}
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          ml: "auto",
          px: 2,
          py: 1,
          borderRadius: 2,
          background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.04)",
          border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.1)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-end",
          minWidth: 160,
        }}
      >
        <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, opacity: 0.5, mb: 0.3 }}>
          Total liquidación
        </Typography>
        <Typography sx={{ fontWeight: 900, fontSize: 20, color: "primary.main", letterSpacing: -0.5 }}>
          {formatCurrency(grand)}
        </Typography>
      </Box>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function LiquidationTab({ legalFile, onActiveChange, onCountChange, onRefreshLegalFile }) {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTitle, setPreviewTitle] = useState("");
  const blobUrlRef = useRef(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const load = useCallback(async () => {
    if (!legalFile?.id) return;
    setLoading(true);
    setError("");
    try {
      const res = await getLiquidationsByLegalFile(legalFile.id);
      const activeData = res.data?.active ?? null;
      const hist = res.data?.history ?? [];
      setActive(activeData);
      setHistory(hist);
      onActiveChange?.(!!activeData);
      onCountChange?.((activeData ? 1 : 0) + hist.length);
    } catch {
      setError("No se pudo cargar la liquidación.");
    } finally {
      setLoading(false);
    }
  }, [legalFile?.id, onActiveChange, onCountChange]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePreview = async (liquidation, label) => {
    if (!liquidation.legal_file_document_id) {
      showToast("Esta liquidación todavía no tiene un PDF generado.", "info");
      return;
    }
    setPreviewLoading(true);
    setPreviewOpen(true);
    setPreviewUrl(null);
    setPreviewTitle(label);
    try {
      const response = await downloadLegalFileDocument(
        liquidation.legal_file_document_id,
      );
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      setPreviewUrl(url);
    } catch {
      showToast("No se pudo cargar el PDF generado.", "error");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setPreviewUrl(null);
  };

  const locked = isLegalFileLocked(legalFile);
  const canCreate = hasPermission("crear liquidacion") && !locked;
  const canDelete = hasPermission("eliminar liquidacion");

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await deleteLiquidation(deleteTarget.id);
      showToast("Liquidación eliminada correctamente.", "success");
      setDeleteTarget(null);
      load();
      onRefreshLegalFile?.();
    } catch {
      showToast("No se pudo eliminar la liquidación.", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  // ── Empty state ───────────────────────────────────────────────────────────
  if (!active) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 4,
            bgcolor: isDark ? "rgba(25,118,210,0.12)" : "rgba(25,118,210,0.08)",
            border: "1px solid rgba(25,118,210,0.2)",
            display: "grid",
            placeItems: "center",
            mx: "auto",
            mb: 1.5,
            color: "primary.main",
          }}
        >
          <CalculateRoundedIcon sx={{ fontSize: 28 }} />
        </Box>
        <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Sin liquidación</Typography>
        <Typography variant="body2" sx={{ opacity: 0.6, mb: 2.5 }}>
          No hay planilla de liquidación para este expediente.
        </Typography>
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={() => setFormOpen(true)}
            sx={{ borderRadius: 2.5, fontWeight: 900 }}
          >
            Crear liquidación
          </Button>
        )}
        <LiquidationFormDialog
          open={formOpen}
          onClose={() => setFormOpen(false)}
          legalFile={legalFile}
          onSaved={() => { load(); onRefreshLegalFile?.(); showToast("Liquidación guardada.", "success"); }}
        />
      </Box>
    );
  }

  // ── Active liquidation ────────────────────────────────────────────────────
  const itemsByCategory = ["capital", "honorarios", "gastos"].map((cat) => ({
    cat,
    items: (active.items ?? []).filter((i) => i.item_type?.category === cat),
  }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

      {/* ── Top bar (matches SectionTitle pattern from LegalFileDetail) ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.2, mb: 0.5 }}>
        <Box sx={{ display: "flex", gap: 1.2, alignItems: "center", minWidth: 0 }}>
          <Box
            sx={(theme) => ({
              width: 42,
              height: 42,
              borderRadius: 3,
              display: "grid",
              placeItems: "center",
              background: theme.palette.mode === "dark" ? "rgba(25,118,210,0.10)" : "rgba(255,255,255,0.72)",
              border: "1px solid rgba(25,118,210,0.22)",
              boxShadow: theme.palette.mode === "dark" ? "none" : "0 10px 26px rgba(0,0,0,0.08)",
              color: "primary.main",
              "& svg": { fontSize: 22, display: "block" },
              flexShrink: 0,
            })}
          >
            <CalculateRoundedIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontWeight: 950, fontSize: 18, lineHeight: 1.15 }}>
                Liquidación activa
              </Typography>
              <GlassBadge isDark={isDark}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", lineHeight: 1, opacity: isDark ? 0.85 : 0.65 }}>
                  v{active.version}
                </Typography>
              </GlassBadge>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Planilla de liquidación del expediente.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          <Tooltip title="Ver PDF generado">
            <IconButton
              size="small"
              onClick={() => handlePreview(active, `Versión ${active.version} (activa)`)}
              sx={{ borderRadius: 2, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", "&:hover": { bgcolor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.07)" } }}
            >
              <VisibilityRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canCreate && (
            <Tooltip title="Nueva versión">
              <IconButton
                size="small"
                onClick={() => setFormOpen(true)}
                sx={{ borderRadius: 2, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", "&:hover": { bgcolor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.07)" } }}
              >
                <AddRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Eliminar esta versión">
              <IconButton
                size="small"
                onClick={() => setDeleteTarget(active)}
                sx={{ borderRadius: 2, color: "error.main", bgcolor: isDark ? "rgba(211,47,47,0.08)" : "rgba(211,47,47,0.05)", "&:hover": { bgcolor: "rgba(211,47,47,0.14)" } }}
              >
                <DeleteRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      {/* ── Version history strip (visible always, above content) ── */}
      {history.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            px: 1.5,
            py: 1,
            borderRadius: 2,
            background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
            border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.06)",
          }}
        >
          <Typography
            sx={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, opacity: 0.4, mr: 0.5 }}
          >
            Versiones anteriores
          </Typography>
          {history.map((liq) => (
            <Box
              key={liq.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.4,
                px: 1.2,
                py: 0.45,
                borderRadius: 1.5,
                background: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.75)",
                border: isDark ? "1px solid rgba(255,255,255,0.09)" : "1px solid rgba(0,0,0,0.09)",
                backdropFilter: "blur(8px)",
              }}
            >
              <Tooltip title={`Ver PDF generado v${liq.version}`}>
                <Box
                  onClick={() => handlePreview(liq, `Versión ${liq.version}`)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                    cursor: "pointer",
                    "&:hover": { opacity: 0.75 },
                  }}
                >
                  <VisibilityRoundedIcon sx={{ fontSize: 13, opacity: 0.5 }} />
                  <Typography sx={{ fontSize: 11, fontWeight: 700 }}>v{liq.version}</Typography>
                  <Typography sx={{ fontSize: 10, opacity: 0.45 }}>{fmtDate(liq.created_at)}</Typography>
                </Box>
              </Tooltip>
              {canDelete && (
                <Tooltip title={`Eliminar v${liq.version}`}>
                  <IconButton
                    size="small"
                    onClick={() => setDeleteTarget(liq)}
                    sx={{ p: 0.3, ml: 0.4, color: "error.main", opacity: 0.6, "&:hover": { opacity: 1, bgcolor: "rgba(211,47,47,0.1)" } }}
                  >
                    <DeleteRoundedIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* ── Metadata pills ── */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <MetaPill label="Monto título" value={formatCurrency(active.lawsuit_amount)} isDark={isDark} />
        <MetaPill label="Fecha título" value={fmtDate(active.lawsuit_date)} isDark={isDark} />
        <MetaPill label="Valor JUS" value={formatCurrency(active.jus_value)} isDark={isDark} />
        <MetaPill label="Tasa anual" value={`${active.annual_rate}%`} isDark={isDark} />
        {active.tributario_date && <MetaPill label="Fecha tributario" value={fmtDate(active.tributario_date)} isDark={isDark} />}
        {active.tributario_balance && <MetaPill label="Saldo tributario" value={formatCurrency(active.tributario_balance)} isDark={isDark} />}
      </Box>

      <Divider />

      {/* ── Items grouped by category ── */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {itemsByCategory.map(({ cat, items }) => (
          <CategorySection
            key={cat}
            category={cat}
            items={items}
            isDark={isDark}
            jusValue={active.jus_value}
            honorariosInJus={active.honorarios_in_jus !== false}
          />
        ))}
      </Box>

      {/* ── Totals ── */}
      <TotalsSection
        items={active.items}
        isDark={isDark}
        jusValue={active.jus_value}
        honorariosInJus={active.honorarios_in_jus !== false}
      />

      {/* ── Notes ── */}
      {active.notes && (
        <Box
          sx={{
            px: 2, py: 1.2, borderRadius: 2,
            background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.6)",
            backdropFilter: "blur(10px)",
            border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.07)",
          }}
        >
          <Typography sx={{ opacity: 0.5, fontWeight: 700, display: "block", fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, mb: 0.5 }}>
            Notas
          </Typography>
          <Typography variant="body2">{active.notes}</Typography>
        </Box>
      )}

      {/* ── Dialogs ── */}
      <LiquidationFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        legalFile={legalFile}
        onSaved={() => { load(); showToast("Liquidación guardada.", "success"); }}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleteLoading}
        title="Eliminar liquidación"
        description={`¿Eliminar la versión ${deleteTarget?.version ?? ""}? Se borrarán los ítems y el PDF generado asociado. Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        confirmColor="error"
      />

      <AppDialog
        open={previewOpen}
        onClose={handleClosePreview}
        title={`PDF generado · ${previewTitle}`}
        maxWidth="xl"
        fullWidth
        stickyHeader
        contentSx={{ p: 0 }}
      >
        {previewLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : previewUrl ? (
          <iframe
            src={previewUrl}
            style={{ width: "100%", height: "78vh", border: 0, display: "block" }}
            title="preview"
          />
        ) : null}
      </AppDialog>
    </Box>
  );
}
